import { onErrorCaptured } from "vue";
import type { ComponentPublicInstance } from "vue";

import { clientStateManager } from "@/services/error-services";
import { createDefaultLogger } from "@/types/console-logger";

const logger = createDefaultLogger();

/**
 * UX roadmap #17: the one way a component boundary (an `onErrorCaptured` hook) reports what it caught. The local
 * boundaries used to call only logger.warn, which is a no-op in production, and return false: every error under an
 * article, a trade alert or a dashboard sector widget stopped there and was never reported at all.
 */

/** Vue's name for where an error was thrown, by error type (runtime-core's ErrorTypeStrings). */
const VueErrorSources: Readonly< Record< string, string > > = {
    sp: 'serverPrefetch hook',
    bc: 'beforeCreate hook',
    c: 'created hook',
    bm: 'beforeMount hook',
    m: 'mounted hook',
    bu: 'beforeUpdate hook',
    u: 'updated',
    bum: 'beforeUnmount hook',
    um: 'unmounted hook',
    a: 'activated hook',
    da: 'deactivated hook',
    ec: 'errorCaptured hook',
    rtc: 'renderTracked hook',
    rtg: 'renderTriggered hook',
    0: 'setup function',
    1: 'render function',
    2: 'watcher getter',
    3: 'watcher callback',
    4: 'watcher cleanup function',
    5: 'native event handler',
    6: 'component event handler',
    7: 'vnode hook',
    8: 'directive hook',
    9: 'transition hook',
    10: 'app errorHandler',
    11: 'app warnHandler',
    12: 'ref function',
    13: 'async component loader',
    14: 'scheduler flush',
    15: 'component update',
    16: 'app unmount cleanup function'
};

/** A production build passes this link plus the error type instead of the text a development build passes. */
const VueErrorReferencePrefix = 'https://vuejs.org/error-reference/#runtime-';

/**
 * Errors after which the component that threw has nothing (or a stale, half-patched tree) on screen. Anything else
 * (an event handler, a watcher, a mounted hook) leaves the rendered section in place, so a boundary only reports it.
 * An async component loader is left out: a lazyView() shows its own LazyViewError, which a fallback would replace.
 */
const RenderBreakingSources: ReadonlySet< string > = new Set( [
    'setup function',
    'render function',
    'component update',
    'beforeCreate hook',
    'created hook',
    'beforeMount hook'
] );

/** Where the error was thrown, as the same text in development and production builds. */
export function vueErrorSource( info: string ): string {
    if ( info.startsWith( VueErrorReferencePrefix ) ) {
        const type = info.slice( VueErrorReferencePrefix.length );
        return VueErrorSources[ type ] ?? info;
    }
    return info;
}

/** Whether the error left the component that threw with nothing to show. */
export function breaksRendering( info: string ): boolean {
    return RenderBreakingSources.has( vueErrorSource( info ) );
}

export interface CapturedErrorContext {
    /** Which boundary caught it: 'page', 'product-tab', 'Article', … */
    boundary: string;
    /** The component that threw, when Vue knows its name. */
    component?: string;
    /** Where in that component: 'render function', 'setup function', 'native event handler', … */
    source: string;
}

function componentName( instance: ComponentPublicInstance | null ): string | undefined {
    const options = instance?.$options as { name?: string, __name?: string } | undefined;
    return options?.name ?? options?.__name;
}

export function capturedErrorContext( boundary: string, instance: ComponentPublicInstance | null, info: string ): CapturedErrorContext {
    return { boundary, component: componentName( instance ), source: vueErrorSource( info ) };
}

/** Queues a captured error for the client error report, and logs it in development (a boundary that returns false keeps Vue from logging it). */
export function reportCapturedError( err: unknown, instance: ComponentPublicInstance | null, info: string, boundary: string ): void {
    const ctx = capturedErrorContext( boundary, instance, info );
    logger.error( `Error captured by the ${ boundary } boundary (${ ctx.component ?? 'anonymous' }, ${ ctx.source })`, err );
    clientStateManager.queueClientError( err, ctx );
}

/**
 * Registers a boundary that reports everything under this component and stops it there. The section stays as it is:
 * for a fallback in place of a section that failed to render, wrap it in <ErrorBoundary> instead.
 */
export function useReportCapturedErrors( boundary: string ): void {
    onErrorCaptured( ( err: unknown, instance: ComponentPublicInstance | null, info: string ) => {
        reportCapturedError( err, instance, info, boundary );
        // Reported here, so App.vue's last-resort hook does not report it again
        return false;
    } );
}
